import { ChevronDown, ChevronUp, Filter, X } from "lucide-react"
import Slider from "rc-slider"
import { useEffect, useState } from "react"
import { useClearRefinements, useCurrentRefinements, useRange, useRefinementList } from "react-instantsearch"
import "rc-slider/assets/index.css"
import { cn } from "../../utils/cn"

interface FiltersProps {
	className?: string
	isMobile?: boolean
	onClose?: () => void
}

/**
 * Filters component provides faceted filtering for search results
 * Desktop: Sidebar layout
 * Mobile: Bottom sheet drawer with overlay
 */
export const Filters = ({ className, isMobile = false, onClose }: FiltersProps) => {
	const { refine: clearAllRefinements } = useClearRefinements()
	const { items: currentRefinements } = useCurrentRefinements()

	const hasActiveFilters = currentRefinements.length > 0

	const handleClearAll = () => {
		clearAllRefinements()
	}

	return (
		<div className={cn("flex flex-col bg-background", isMobile ? "h-full" : "", className)}>
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b border-border">
				<div className="flex items-center gap-2">
					<Filter className="w-5 h-5" />
					<h2 className="font-semibold text-lg">Filters</h2>
				</div>
				<div className="flex items-center gap-2">
					{hasActiveFilters && (
						<button className="text-sm text-primary hover:text-primary/80 transition-colors" onClick={handleClearAll}>
							Clear All
						</button>
					)}
					{isMobile && onClose && (
						<button
							aria-label="Close filters"
							className="p-1 rounded-md hover:bg-accent transition-colors"
							onClick={onClose}>
							<X className="w-5 h-5" />
						</button>
					)}
				</div>
			</div>

			{/* Filter Groups */}
			<div className="flex-1 overflow-y-auto p-4 space-y-6 pb-6">
				{/* Category Filter */}
				<CategoryFilter />

				{/* Brand Filter */}
				<BrandFilter />

				{/* Price Range Filter */}
				<PriceRangeFilter />

				{/* In Stock Filter */}
				<InStockFilter />
			</div>

			{/* Mobile Footer */}
			{isMobile && (
				<div className="p-4 border-t border-border">
					<button
						className="w-full py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
						onClick={onClose}>
						Apply Filters
					</button>
				</div>
			)}
		</div>
	)
}

/**
 * CategoryFilter - Faceted category refinement with "Show More" functionality
 */
const CategoryFilter = () => {
	const [isExpanded, setIsExpanded] = useState(true)
	const [showAll, setShowAll] = useState(false)
	const { items, refine } = useRefinementList({
		attribute: "category",
		limit: showAll ? 100 : 10,
		sortBy: ["count:desc", "name:asc"],
	})

	if (items.length === 0) return null

	const hasMore = items.length >= (showAll ? 100 : 10)

	return (
		<div className="space-y-2">
			<button className="flex items-center justify-between w-full text-left" onClick={() => setIsExpanded(!isExpanded)}>
				<h3 className="font-medium">Category</h3>
				{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
			</button>

			{isExpanded && (
				<div className="space-y-2 pl-1">
					<div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
						{items.map((item) => (
							<label className="flex items-center gap-2 cursor-pointer group" key={item.value}>
								<input
									checked={item.isRefined}
									className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
									onChange={() => refine(item.value)}
									type="checkbox"
								/>
								<span className="flex-1 text-sm group-hover:text-primary transition-colors">{item.label}</span>
								<span className="text-xs text-muted-foreground">({item.count})</span>
							</label>
						))}
					</div>
					{hasMore && !showAll && (
						<button
							className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
							onClick={() => setShowAll(true)}>
							Show More
						</button>
					)}
					{showAll && (
						<button
							className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
							onClick={() => setShowAll(false)}>
							Show Less
						</button>
					)}
				</div>
			)}
		</div>
	)
}

/**
 * BrandFilter - Faceted brand refinement with "Show More" functionality
 */
const BrandFilter = () => {
	const [isExpanded, setIsExpanded] = useState(true)
	const [showAll, setShowAll] = useState(false)
	const { items, refine } = useRefinementList({
		attribute: "brand",
		limit: showAll ? 100 : 10,
		sortBy: ["count:desc", "name:asc"],
	})

	if (items.length === 0) return null

	const hasMore = items.length >= (showAll ? 100 : 10)

	return (
		<div className="space-y-2">
			<button className="flex items-center justify-between w-full text-left" onClick={() => setIsExpanded(!isExpanded)}>
				<h3 className="font-medium">Brand</h3>
				{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
			</button>

			{isExpanded && (
				<div className="space-y-2 pl-1">
					<div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
						{items.map((item) => (
							<label className="flex items-center gap-2 cursor-pointer group" key={item.value}>
								<input
									checked={item.isRefined}
									className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
									onChange={() => refine(item.value)}
									type="checkbox"
								/>
								<span className="flex-1 text-sm group-hover:text-primary transition-colors">{item.label}</span>
								<span className="text-xs text-muted-foreground">({item.count})</span>
							</label>
						))}
					</div>
					{hasMore && !showAll && (
						<button
							className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
							onClick={() => setShowAll(true)}>
							Show More
						</button>
					)}
					{showAll && (
						<button
							className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
							onClick={() => setShowAll(false)}>
							Show Less
						</button>
					)}
				</div>
			)}
		</div>
	)
}

/**
 * PriceRangeFilter - Dual-thumb range slider for price filtering
 * Note: Price is not a facet field, so we use useRange for numeric filtering
 */
const PriceRangeFilter = () => {
	const [isExpanded, setIsExpanded] = useState(true)

	// Price range filtering - this works without faceting
	const { start, range, refine } = useRange({
		attribute: "price",
	})

	console.log("[PriceRangeFilter] range:", range, "start:", start)

	const min = range.min ?? 0
	const max = range.max ?? 10000
	const currentMin = start[0] ?? min
	const currentMax = start[1] ?? max

	const [values, setValues] = useState([currentMin, currentMax])

	// Update local state when remote state changes (e.g. clear filters)
	useEffect(() => {
		setValues([currentMin, currentMax])
	}, [currentMin, currentMax])

	// Don't show if no price range available
	if (range.min === undefined || range.max === undefined || range.min === range.max) {
		return null
	}

	const handleSliderChange = (newValues: number | number[]) => {
		const valuesArray = Array.isArray(newValues) ? newValues : [newValues, newValues]
		setValues(valuesArray)
		refine([valuesArray[0], valuesArray[1]])
	}

	const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Math.min(Number(e.target.value), values[1] - 1)
		const newValues: [number, number] = [value, values[1]]
		setValues(newValues)
		refine(newValues)
	}

	const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Math.max(Number(e.target.value), values[0] + 1)
		const newValues: [number, number] = [values[0], value]
		setValues(newValues)
		refine(newValues)
	}

	return (
		<div className="space-y-2">
			<button className="flex items-center justify-between w-full text-left" onClick={() => setIsExpanded(!isExpanded)}>
				<h3 className="font-medium">Price Range</h3>
				{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
			</button>

			{isExpanded && (
				<div className="space-y-4 pl-1">
					<div className="flex items-center gap-2 text-sm">
						<span className="text-muted-foreground">₹</span>
						<input
							className="w-20 px-2 py-1 rounded border border-input bg-background text-sm"
							max={values[1]}
							min={min}
							onChange={handleMinInputChange}
							type="number"
							value={values[0]}
						/>
						<span className="text-muted-foreground">-</span>
						<span className="text-muted-foreground">₹</span>
						<input
							className="w-20 px-2 py-1 rounded border border-input bg-background text-sm"
							max={max}
							min={values[0]}
							onChange={handleMaxInputChange}
							type="number"
							value={values[1]}
						/>
					</div>

					<div className="px-2 py-4">
						<Slider
							max={max}
							min={min}
							onChange={handleSliderChange}
							range
							styles={{
								track: {
									backgroundColor: "hsl(var(--muted))",
								},
								tracks: {
									backgroundColor: "hsl(var(--primary))",
								},
								handle: {
									backgroundColor: "hsl(var(--primary))",
									borderColor: "hsl(var(--primary))",
									opacity: 1,
								},
								rail: {
									backgroundColor: "hsl(var(--muted))",
								},
							}}
							value={values}
						/>
					</div>
				</div>
			)}
		</div>
	)
}

/**
 * InStockFilter - Toggle for in-stock products only
 */
const InStockFilter = () => {
	const [isExpanded, setIsExpanded] = useState(true)
	const { items, refine } = useRefinementList({
		attribute: "inStock",
		limit: 2,
	})

	if (items.length === 0) return null

	const inStockItem = items.find((item) => String(item.value) === "true")

	if (!inStockItem) return null

	return (
		<div className="space-y-2">
			<button className="flex items-center justify-between w-full text-left" onClick={() => setIsExpanded(!isExpanded)}>
				<h3 className="font-medium">Availability</h3>
				{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
			</button>

			{isExpanded && (
				<div className="pl-1">
					<label className="flex items-center gap-2 cursor-pointer group">
						<input
							checked={inStockItem.isRefined}
							className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
							onChange={() => refine(inStockItem.value)}
							type="checkbox"
						/>
						<span className="flex-1 text-sm group-hover:text-primary transition-colors">In Stock Only</span>
						<span className="text-xs text-muted-foreground">({inStockItem.count})</span>
					</label>
				</div>
			)}
		</div>
	)
}

/**
 * MobileFiltersDrawer - Mobile bottom sheet for filters
 */
export const MobileFiltersDrawer = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
	if (!isOpen) return null

	return (
		<>
			{/* Overlay */}
			<div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />

			{/* Drawer */}
			<div className="fixed inset-x-0 bottom-0 z-50 lg:hidden max-h-[80vh] rounded-t-2xl overflow-hidden shadow-xl">
				<Filters isMobile onClose={onClose} />
			</div>
		</>
	)
}
